/**
 * HelpPage - User Guide & Examples
 *
 * Provides:
 * - Step-by-step workflow guide
 * - HTML dashboard examples
 * - Tips for candidate info
 * - AI consultation tip
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getUserCredits } from '../../services/userService';

// Icons
const LogoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-8 h-8">
    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
    <path d="M2 17l10 5 10-5"/>
    <path d="M2 12l10 5 10-5"/>
  </svg>
);

const ChartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <line x1="18" y1="20" x2="18" y2="10"/>
    <line x1="12" y1="20" x2="12" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="14"/>
  </svg>
);

const HelpIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <circle cx="12" cy="12" r="10"/>
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const LogoutIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

const CandidateIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const AdminIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const CreditCardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
    <line x1="1" y1="10" x2="23" y2="10"/>
  </svg>
);

const BookIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
  </svg>
);

const RocketIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6">
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
    <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
  </svg>
);

const SparkleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6">
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
  </svg>
);

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const HelpPage: React.FC = () => {
  const { user, signOut, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [credits, setCredits] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<'workflow' | 'examples' | 'tips'>('workflow');

  // Load credits
  React.useEffect(() => {
    const loadCredits = async () => {
      if (user?.uid) {
        const userCredits = await getUserCredits(user.uid);
        setCredits(userCredits);
      }
    };
    loadCredits();
  }, [user?.uid]);

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      {/* Header */}
      <header
        className="sticky top-0 z-50 backdrop-blur-xl border-b"
        style={{
          background: 'rgba(var(--bg-primary-rgb), 0.8)',
          borderColor: 'var(--border-light)'
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="h-16 flex items-center justify-between">
            {/* Logo */}
            <Link to="/filter" className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-white"
                style={{
                  background: 'linear-gradient(135deg, var(--phoenix-500) 0%, var(--phoenix-700) 100%)',
                  boxShadow: '0 4px 12px rgba(0, 70, 140, 0.3)'
                }}
              >
                <LogoIcon />
              </div>
              <span className="font-display text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                Phoenix Analytics
              </span>
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              <Link
                to="/filter"
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                style={{ color: 'var(--text-secondary)' }}
              >
                <ChartIcon />
                Analýza
              </Link>
              <Link
                to="/candidate"
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                style={{ color: 'var(--text-secondary)' }}
              >
                <CandidateIcon />
                Kandidáti
              </Link>
              <Link
                to="/billing"
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                style={{ color: 'var(--text-secondary)' }}
              >
                <CreditCardIcon />
                Platby
              </Link>
              <Link
                to="/help"
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                style={{ background: 'var(--phoenix-50)', color: 'var(--phoenix-700)' }}
              >
                <HelpIcon />
                Nápověda
              </Link>
              {isAdmin && (
                <Link
                  to="/admin"
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <AdminIcon />
                  Admin
                </Link>
              )}
            </nav>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-3 px-3 py-2 rounded-xl transition-colors"
                style={{ background: showUserMenu ? 'var(--bg-tertiary)' : 'transparent' }}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-medium"
                  style={{ background: 'linear-gradient(135deg, var(--phoenix-400) 0%, var(--phoenix-600) 100%)' }}
                >
                  {user?.email?.charAt(0).toUpperCase()}
                </div>
                <span className="hidden sm:block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  {user?.email?.split('@')[0]}
                </span>
              </button>

              {showUserMenu && (
                <div
                  className="absolute right-0 mt-2 w-64 rounded-xl shadow-xl border overflow-hidden"
                  style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-light)' }}
                >
                  <div className="p-4 border-b" style={{ borderColor: 'var(--border-light)' }}>
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-medium"
                        style={{ background: 'linear-gradient(135deg, var(--phoenix-400) 0%, var(--phoenix-600) 100%)' }}
                      >
                        {user?.email?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>
                          {user?.email?.split('@')[0]}
                        </div>
                        <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                          {user?.email}
                        </div>
                      </div>
                    </div>
                    {/* Credits display */}
                    <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--border-light)' }}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Kredity:</span>
                        <span className="text-sm font-semibold" style={{ color: 'var(--phoenix-600)' }}>
                          {isAdmin ? '∞' : credits}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="p-2">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors"
                      style={{ color: 'var(--danger-600)' }}
                    >
                      <LogoutIcon />
                      Odhlásit se
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="font-display text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
            Nápověda & Příklady
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            Kompletní průvodce používáním Phoenix Electoral Analytics
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b" style={{ borderColor: 'var(--border-light)' }}>
          <button
            onClick={() => setActiveTab('workflow')}
            className="px-4 py-3 text-sm font-medium transition-colors relative"
            style={{
              color: activeTab === 'workflow' ? 'var(--phoenix-600)' : 'var(--text-secondary)',
              borderBottom: activeTab === 'workflow' ? '2px solid var(--phoenix-600)' : '2px solid transparent'
            }}
          >
            <div className="flex items-center gap-2">
              <RocketIcon />
              Jak to funguje
            </div>
          </button>
          <button
            onClick={() => setActiveTab('examples')}
            className="px-4 py-3 text-sm font-medium transition-colors relative"
            style={{
              color: activeTab === 'examples' ? 'var(--phoenix-600)' : 'var(--text-secondary)',
              borderBottom: activeTab === 'examples' ? '2px solid var(--phoenix-600)' : '2px solid transparent'
            }}
          >
            <div className="flex items-center gap-2">
              <BookIcon />
              Ukázky výstupů
            </div>
          </button>
          <button
            onClick={() => setActiveTab('tips')}
            className="px-4 py-3 text-sm font-medium transition-colors relative"
            style={{
              color: activeTab === 'tips' ? 'var(--phoenix-600)' : 'var(--text-secondary)',
              borderBottom: activeTab === 'tips' ? '2px solid var(--phoenix-600)' : '2px solid transparent'
            }}
          >
            <div className="flex items-center gap-2">
              <SparkleIcon />
              Tipy & AI konzultace
            </div>
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'workflow' && (
          <div className="space-y-8">
            {/* Step 1 */}
            <div
              className="p-6 rounded-2xl border"
              style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-light)' }}
            >
              <div className="flex items-start gap-4">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold shrink-0"
                  style={{ background: 'linear-gradient(135deg, var(--phoenix-500) 0%, var(--phoenix-700) 100%)' }}
                >
                  1
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                    Vyplňte základní parametry
                  </h3>
                  <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
                    Na stránce <strong>Analýza</strong> vyberte:
                  </p>
                  <ul className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <li className="flex items-start gap-2">
                      <span style={{ color: 'var(--phoenix-600)' }}>•</span>
                      <span><strong>Typ voleb:</strong> Komunální nebo Senátní</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span style={{ color: 'var(--phoenix-600)' }}>•</span>
                      <span><strong>Obec/Obvod:</strong> Začněte psát název a vyberte ze seznamu</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span style={{ color: 'var(--phoenix-600)' }}>•</span>
                      <span><strong>Strana (volitelné):</strong> Pro detailní analýzu konkrétní strany</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div
              className="p-6 rounded-2xl border"
              style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-light)' }}
            >
              <div className="flex items-start gap-4">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold shrink-0"
                  style={{ background: 'linear-gradient(135deg, var(--phoenix-500) 0%, var(--phoenix-700) 100%)' }}
                >
                  2
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                    Doplňte informace o kandidátech (volitelné)
                  </h3>
                  <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
                    Máte <strong>dvě možnosti</strong>, jak zadat informace o kandidátech:
                  </p>

                  {/* Option A - Text field */}
                  <div
                    className="p-4 rounded-xl mb-4"
                    style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-light)' }}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: 'var(--phoenix-600)' }}>A</span>
                      <span className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>Textové pole na stránce Analýza</span>
                    </div>
                    <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
                      Do pole <strong>"Doplňující informace"</strong> napište volný text:
                    </p>
                    <div
                      className="p-3 rounded-lg font-mono text-xs"
                      style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                    >
                      <pre className="whitespace-pre-wrap leading-relaxed">
{`Lídr: Jan Novák, 45 let, podnikatel
Konkurence: ODS má starostu 2 období
Témata: doprava, bezpečnost, senior taxi`}
                      </pre>
                    </div>
                  </div>

                  {/* Option B - Candidate page */}
                  <div
                    className="p-4 rounded-xl mb-4"
                    style={{ background: 'var(--phoenix-50)', border: '1px solid var(--phoenix-200)' }}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: 'var(--phoenix-600)' }}>B</span>
                      <span className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>Stránka Kandidáti s nahráním PDF</span>
                    </div>
                    <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
                      Na stránce <Link to="/candidate" className="font-semibold underline" style={{ color: 'var(--phoenix-600)' }}>Kandidáti</Link> můžete:
                    </p>
                    <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      <li className="flex items-start gap-2">
                        <span style={{ color: 'var(--phoenix-600)' }}>•</span>
                        <span><strong>Nahrát PDF</strong> s životopisy kandidátů</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span style={{ color: 'var(--phoenix-600)' }}>•</span>
                        <span><strong>Vložit text</strong> s informacemi o kandidátech</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span style={{ color: 'var(--phoenix-600)' }}>•</span>
                        <span>AI automaticky <strong>extrahuje strukturovaná data</strong></span>
                      </li>
                    </ul>
                  </div>

                  <div
                    className="p-4 rounded-xl"
                    style={{ background: 'var(--success-50)', border: '1px solid var(--success-200)' }}
                  >
                    <p className="text-sm" style={{ color: 'var(--success-700)' }}>
                      <strong>Tip:</strong> Čím více informací poskytnete, tím přesnější bude strategická analýza.
                      AI zpracuje i nestrukturovaný text - pište volně, jak vás napadne.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div
              className="p-6 rounded-2xl border"
              style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-light)' }}
            >
              <div className="flex items-start gap-4">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold shrink-0"
                  style={{ background: 'linear-gradient(135deg, var(--phoenix-500) 0%, var(--phoenix-700) 100%)' }}
                >
                  3
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                    Spusťte analýzu a počkejte na email
                  </h3>
                  <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
                    Po kliknutí na <strong>"Spustit analýzu"</strong> dostanete:
                  </p>
                  <ul className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <li className="flex items-start gap-2">
                      <span style={{ color: 'var(--phoenix-600)' }}>•</span>
                      <span><strong>HTML Dashboard</strong> - interaktivní vizualizace s grafy a tabulkami</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span style={{ color: 'var(--phoenix-600)' }}>•</span>
                      <span><strong>PDF Report</strong> - profesionální dokument pro tisk</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span style={{ color: 'var(--phoenix-600)' }}>•</span>
                      <span><strong>Markdown soubor</strong> - strukturovaný text pro další zpracování</span>
                    </li>
                  </ul>

                  <div
                    className="mt-4 p-4 rounded-xl"
                    style={{ background: 'var(--phoenix-50)', border: '1px solid var(--phoenix-200)' }}
                  >
                    <p className="text-sm" style={{ color: 'var(--phoenix-700)' }}>
                      <strong>Doba zpracování:</strong> Cca 2-5 minut. Email přijde na adresu,
                      pod kterou jste přihlášeni.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'examples' && (
          <div className="space-y-8">
            {/* HTML Dashboard Preview */}
            <div
              className="p-6 rounded-2xl border"
              style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-light)' }}
            >
              <h3 className="font-display text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                HTML Dashboard - ukázka
              </h3>

              {/* Mock dashboard preview */}
              <div
                className="rounded-xl overflow-hidden border"
                style={{ borderColor: 'var(--border-light)' }}
              >
                {/* Mock header */}
                <div
                  className="p-4 flex items-center justify-between"
                  style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                >
                  <div className="text-white">
                    <div className="font-bold">Phoenix Electoral Analytics</div>
                    <div className="text-sm opacity-80">Komunální volby 2022 - Havlíčkův Brod</div>
                  </div>
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-white/30"></div>
                    <div className="w-3 h-3 rounded-full bg-white/30"></div>
                  </div>
                </div>

                {/* Mock stats */}
                <div className="p-4 grid grid-cols-4 gap-4" style={{ background: 'var(--bg-tertiary)' }}>
                  <div className="text-center">
                    <div className="text-2xl font-bold" style={{ color: 'var(--phoenix-600)' }}>12,458</div>
                    <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Celkem hlasů</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold" style={{ color: 'var(--phoenix-600)' }}>8</div>
                    <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Kandidujících stran</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold" style={{ color: 'var(--phoenix-600)' }}>42.3%</div>
                    <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Volební účast</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold" style={{ color: 'var(--success-600)' }}>98.7%</div>
                    <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Kvalita dat</div>
                  </div>
                </div>

                {/* Mock table */}
                <div className="p-4" style={{ background: 'var(--bg-primary)' }}>
                  <div className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                    Top 3 strany
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ color: 'var(--text-tertiary)' }}>
                        <th className="text-left py-1">#</th>
                        <th className="text-left py-1">Strana</th>
                        <th className="text-right py-1">Hlasy</th>
                        <th className="text-right py-1">%</th>
                        <th className="text-right py-1">Mandáty</th>
                      </tr>
                    </thead>
                    <tbody style={{ color: 'var(--text-primary)' }}>
                      <tr>
                        <td className="py-1">🥇</td>
                        <td className="py-1">ODS</td>
                        <td className="py-1 text-right">3,245</td>
                        <td className="py-1 text-right">26.0%</td>
                        <td className="py-1 text-right font-bold">7</td>
                      </tr>
                      <tr>
                        <td className="py-1">🥈</td>
                        <td className="py-1">ANO 2011</td>
                        <td className="py-1 text-right">2,890</td>
                        <td className="py-1 text-right">23.2%</td>
                        <td className="py-1 text-right font-bold">6</td>
                      </tr>
                      <tr>
                        <td className="py-1">🥉</td>
                        <td className="py-1">STAN</td>
                        <td className="py-1 text-right">1,567</td>
                        <td className="py-1 text-right">12.6%</td>
                        <td className="py-1 text-right font-bold">3</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <strong>Dashboard obsahuje:</strong>
                <ul className="mt-2 space-y-1">
                  <li>• Přehled výsledků s vizualizacemi</li>
                  <li>• Interaktivní grafy (koláčový, sloupcový)</li>
                  <li>• Tabulky kandidátů s preferenčními hlasy</li>
                  <li>• Mapu okrsků s výsledky</li>
                  <li>• Historické porovnání</li>
                  <li>• Analytické metriky (Gini, HHI, ENP)</li>
                </ul>
              </div>
            </div>

            {/* Output formats */}
            <div className="grid md:grid-cols-3 gap-4">
              {/* HTML */}
              <div
                className="p-5 rounded-xl border"
                style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-light)' }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: 'var(--phoenix-100)' }}
                >
                  <span className="text-2xl">🌐</span>
                </div>
                <h4 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>HTML Dashboard</h4>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Interaktivní webová stránka s grafy, tabulkami a mapou.
                  Otevřete v prohlížeči, sdílejte s týmem.
                </p>
              </div>

              {/* PDF */}
              <div
                className="p-5 rounded-xl border"
                style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-light)' }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: 'var(--danger-100)' }}
                >
                  <span className="text-2xl">📄</span>
                </div>
                <h4 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>PDF Report</h4>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Profesionální dokument vhodný pro tisk a prezentace.
                  Obsahuje všechna data a doporučení.
                </p>
              </div>

              {/* Markdown */}
              <div
                className="p-5 rounded-xl border"
                style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-light)' }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: 'var(--success-100)' }}
                >
                  <span className="text-2xl">📝</span>
                </div>
                <h4 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Markdown</h4>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Strukturovaný textový soubor pro další zpracování.
                  Ideální pro AI konzultace (viz tipy).
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'tips' && (
          <div className="space-y-8">
            {/* Candidate tips */}
            <div
              className="p-6 rounded-2xl border"
              style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-light)' }}
            >
              <h3 className="font-display text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Co napsat o kandidátech?
              </h3>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-2 text-sm" style={{ color: 'var(--success-600)' }}>
                    ✅ Užitečné informace
                  </h4>
                  <ul className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <li>• Věk, profese, zkušenosti kandidátů</li>
                    <li>• Předchozí politická činnost</li>
                    <li>• Silné stránky a úspěchy</li>
                    <li>• Hlavní témata kampaně</li>
                    <li>• Informace o konkurenci</li>
                    <li>• Specifika obce/obvodu</li>
                    <li>• Rozpočet a zdroje</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 text-sm" style={{ color: 'var(--warning-600)' }}>
                    💡 Příklady formulací
                  </h4>
                  <ul className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <li>• "Máme 3 zastupitele z minulého období"</li>
                    <li>• "Starosta je z ODS, vládne 12 let"</li>
                    <li>• "V obci žije hodně seniorů (35%)"</li>
                    <li>• "Hlavní téma: doprava a parkování"</li>
                    <li>• "ANO slibuje novou sportovní halu"</li>
                    <li>• "Máme podporu místních podnikatelů"</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* AI Consultation - MAIN TIP */}
            <div
              className="p-6 rounded-2xl border-2"
              style={{
                background: 'linear-gradient(135deg, var(--phoenix-50) 0%, var(--bg-secondary) 100%)',
                borderColor: 'var(--phoenix-300)'
              }}
            >
              <div className="flex items-start gap-4">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
                  style={{ background: 'linear-gradient(135deg, var(--phoenix-500) 0%, var(--phoenix-700) 100%)' }}
                >
                  <SparkleIcon />
                  <span className="text-white text-2xl">🤖</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-xl font-bold mb-2" style={{ color: 'var(--phoenix-700)' }}>
                    Pro tip: AI konzultace s výstupy
                  </h3>
                  <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
                    Výstupy z Phoenix Analytics můžete <strong>přímo nahrát do AI asistentů</strong>
                    pro další konzultaci a strategické plánování:
                  </p>

                  <div className="grid md:grid-cols-3 gap-4 mb-4">
                    <div
                      className="p-4 rounded-xl"
                      style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-light)' }}
                    >
                      <div className="font-semibold text-sm mb-1" style={{ color: 'var(--text-primary)' }}>
                        ChatGPT
                      </div>
                      <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                        Nahrajte PDF nebo MD soubor a ptejte se na strategii
                      </p>
                    </div>
                    <div
                      className="p-4 rounded-xl"
                      style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-light)' }}
                    >
                      <div className="font-semibold text-sm mb-1" style={{ color: 'var(--text-primary)' }}>
                        Claude
                      </div>
                      <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                        Ideální pro dlouhé dokumenty a komplexní analýzu
                      </p>
                    </div>
                    <div
                      className="p-4 rounded-xl"
                      style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-light)' }}
                    >
                      <div className="font-semibold text-sm mb-1" style={{ color: 'var(--text-primary)' }}>
                        Gemini
                      </div>
                      <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                        Dobré pro rychlé odpovědi a brainstorming
                      </p>
                    </div>
                  </div>

                  <div
                    className="p-4 rounded-xl mb-4"
                    style={{ background: 'var(--bg-primary)', border: '1px solid var(--phoenix-200)' }}
                  >
                    <div className="font-semibold text-sm mb-2" style={{ color: 'var(--phoenix-700)' }}>
                      Příklad promptu pro AI:
                    </div>
                    <p className="text-sm font-mono" style={{ color: 'var(--text-primary)' }}>
                      "Přikládám volební analýzu pro naši stranu. Na základě dat navrhni:
                      <br/>1) Tři hlavní témata kampaně
                      <br/>2) Jak oslovit nerozhodnuté voliče
                      <br/>3) Slabiny konkurence, které můžeme využít"
                    </p>
                  </div>

                  <div
                    className="p-4 rounded-xl"
                    style={{ background: 'var(--success-50)', border: '1px solid var(--success-200)' }}
                  >
                    <p className="text-sm" style={{ color: 'var(--success-700)' }}>
                      <strong>Doporučení:</strong> Použijte <strong>Markdown (.md)</strong> soubor -
                      je nejlépe strukturovaný pro AI zpracování. Případně nahrajte přímo
                      <strong> HTML dashboard</strong> do Claude nebo ChatGPT.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick tips */}
            <div className="grid md:grid-cols-2 gap-4">
              <div
                className="p-5 rounded-xl border"
                style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-light)' }}
              >
                <h4 className="font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                  ⚡ Rychlé tipy
                </h4>
                <ul className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <li>• Analýza bez strany = přehled celé obce</li>
                  <li>• Analýza se stranou = detailní PVI kandidátů</li>
                  <li>• Historická data jsou od roku 2010</li>
                  <li>• Senátní volby: vyberte správný obvod</li>
                </ul>
              </div>

              <div
                className="p-5 rounded-xl border"
                style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-light)' }}
              >
                <h4 className="font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                  📧 Podpora
                </h4>
                <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
                  Máte otázky nebo potřebujete více kreditů?
                </p>
                <a
                  href="mailto:pecka@dwx.cz"
                  className="inline-flex items-center gap-2 text-sm font-medium"
                  style={{ color: 'var(--phoenix-600)' }}
                >
                  <span>pecka@dwx.cz</span>
                  <span>→</span>
                </a>
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 text-center">
          <Link
            to="/filter"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-medium text-white transition-all"
            style={{
              background: 'linear-gradient(135deg, var(--phoenix-500) 0%, var(--phoenix-700) 100%)',
              boxShadow: '0 4px 12px rgba(0, 70, 140, 0.3)'
            }}
          >
            <ChartIcon />
            Spustit analýzu
          </Link>
        </div>
      </main>
    </div>
  );
};

export default HelpPage;
